import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BellIcon, CheckIcon, ChevronRightIcon, ConstructionIcon, DropletsIcon, HelpCircleIcon, LampIcon, LocateFixedIcon, MailIcon, MessageSquareIcon, PawPrintIcon, PaperclipIcon, PlusIcon, SendIcon, SnowflakeIcon, PhoneIcon, SirenIcon, TriangleAlertIcon, Trash2Icon, TreesIcon, XIcon } from 'lucide-react';
import { detectedPosition, fountainPhotoUrl, intentPhotos, potholePhotoUrl, manualAddresses, manualCategories, type CitizenRequest, type RequestAttachment } from '../data/demandes';
import { profile } from '../data/profil';
import { useRequests, type NotifyChannel } from '../contexts/RequestsContext';
import { AddressAutocomplete } from '../components/demandes/AddressAutocomplete';
import { FormAtnaHint, PhotoAtnaHint } from '../components/demandes/FormAtnaHint';
import { LostPetPicker } from '../components/demandes/LostPetPicker';
import { pets } from '../data/animaux';
type CategoryId = (typeof manualCategories)[number]['id'];
type AddressChoice = 'position' | (typeof manualAddresses)[number]['id'] | 'autre';
const addressOptions: {
  id: AddressChoice;
  label: string;
  address: string;
}[] = [{
  id: 'position',
  label: 'Ma position actuelle',
  address: detectedPosition
}, ...manualAddresses.map(item => ({
  id: item.id,
  label: item.label,
  address: item.address
})), {
  id: 'autre',
  label: 'Autre endroit',
  address: 'Adresse ou intersection'
}];
const notifyOptions: {
  id: NotifyChannel;
  label: string;
  Icon: typeof MailIcon;
}[] = [{
  id: 'courriel',
  label: 'Par courriel',
  Icon: MailIcon
}, {
  id: 'sms',
  label: 'Par SMS',
  Icon: MessageSquareIcon
}];
const categoryIcons: Record<CategoryId, React.ComponentType<{
  className?: string;
}>> = {
  voirie: ConstructionIcon,
  eclairage: LampIcon,
  arbres: TreesIcon,
  eau: DropletsIcon,
  collectes: Trash2Icon,
  animaux: PawPrintIcon,
  deneigement: SnowflakeIcon,
  autre: HelpCircleIcon
};

/** Photo de démo « téléversée » selon le problème choisi. */
function demoPhotoFor(type: string): string {
  return intentPhotos[type] ?? potholePhotoUrl;
}
const baseField = 'mt-1.5 w-full rounded-lg border px-3.5 py-2.5 text-sm text-ville-navy focus:outline-none focus:ring-2';
const fieldOk = 'border-slate-300 focus:border-ville-indigo focus:ring-ville-indigo/20';
const fieldError = 'border-red-500 focus:border-red-500 focus:ring-red-500/20';
const labelClass = 'block text-[13px] font-bold text-ville-navy';
const requiredMark = <span className="text-ville-coral" data-id="element-3020" data-mp="25uxuorurz7ll"> *</span>;
export function DemandeFormulaire() {
  const {
    requests,
    addRequest,
    notifyChannels,
    toggleNotifyChannel
  } = useRequests();
  const uploadTimer = useRef<number | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<CategoryId | null>(null);
  // Type « Urgence » : la demande en ligne est bloquée, on renvoie vers le 911.
  const [emergency, setEmergency] = useState(false);
  const [type, setType] = useState('');
  const [petId, setPetId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [addressChoice, setAddressChoice] = useState<AddressChoice>('position');
  const [otherAddress, setOtherAddress] = useState('');
  const [precision, setPrecision] = useState('');
  const [files, setFiles] = useState<RequestAttachment[]>([]);
  const [channels, setChannels] = useState<NotifyChannel[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [created, setCreated] = useState<CitizenRequest | null>(null);
  const category = manualCategories.find(item => item.id === categoryId);
  const notify = created ? (notifyChannels[created.id] ?? []).length > 0 : false;
  const toggleChannel = (channel: NotifyChannel) => setChannels(current => current.includes(channel) ? current.filter(item => item !== channel) : [...current, channel]);
  const errors = {
    category: !categoryId,
    type: !type,
    pet: type === 'Animal perdu' && !petId,
    description: description.trim().length < 10,
    address: addressChoice === 'autre' && otherAddress.trim().length === 0
  };
  const show = (key: keyof typeof errors) => submitted && errors[key];
  const chooseCategory = (id: CategoryId | null) => {
    setCategoryId(id);
    setType('');
    setEmergency(false);
  };

  // Téléversement simulé, comme dans ATNA : aucun sélecteur de fichier ne s'ouvre.
  const simulateUpload = () => {
    if (uploading || files.length >= 5) return;
    const photo = demoPhotoFor(type);
    const index = files.length + 1;
    const file: RequestAttachment = {
      name: index === 1 ? 'photo-signalement.jpg' : `photo-signalement-${index}.jpg`,
      type: 'image',
      size: '1,9 Mo',
      url: photo
    };
    setFiles(current => [...current, file]);
    setUploading(file.name);
    if (uploadTimer.current) window.clearTimeout(uploadTimer.current);
    uploadTimer.current = window.setTimeout(() => setUploading(null), 900);
  };
  useEffect(() => () => {
    if (uploadTimer.current) window.clearTimeout(uploadTimer.current);
  }, []);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.values(errors).some(Boolean) || !category) return;
    const profileAddress = manualAddresses.find(item => item.id === addressChoice);
    const address = profileAddress ? profileAddress.address : addressChoice === 'position' ? detectedPosition : otherAddress.trim();
    const postal = profileAddress ? `, ${profileAddress.postal}` : '';
    const number = 3012 + requests.length;
    const lostPet = type === 'Animal perdu' ? pets.find(pet => pet.id === petId) : undefined;
    const baseDescription = lostPet ? `${lostPet.name} — ${lostPet.species.toLowerCase()}, ${lostPet.breed}, médaille ${lostPet.medal}, puce ${lostPet.microchip}.\n\n${description.trim()}` : description.trim();
    const fullDescription = precision.trim() ? `${baseDescription}\n\nPrécision sur le lieu : ${precision.trim()}` : baseDescription;
    const request: CitizenRequest = {
      id: `req-${number}`,
      reference: `#REQ-${number}`,
      title: lostPet ? `Animal perdu — ${lostPet.name}` : type,
      service: category.service,
      category: category.label,
      address,
      fullAddress: `${address}, Brossard, Québec${postal}`,
      submittedOn: 'Envoyée aujourd’hui',
      createdOn: '30/09/26',
      resolvedOn: null,
      createdBy: 'Charlotte Bouchard',
      status: 'recue',
      description: fullDescription,
      attachments: files,
      timeline: [{
        date: 'Aujourd’hui',
        label: 'Demande reçue par formulaire'
      }],
      messages: []
    };
    addRequest(request);
    // Canaux d'avis partagés avec la fiche de la requête.
    channels.forEach(channel => toggleNotifyChannel(request.id, channel));
    setCreated(request);
  };
  if (created) {
    return <main className="bg-white" data-id="element-3021" data-mp="2hdgmbvoo32ha">
        <div className="mx-auto max-w-[760px] px-5 py-16 lg:px-10" data-id="element-3022" data-mp="2lcxtqunnm9p3">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-3023" data-mp="2lb4lxc1b1jfi">
            <CheckIcon className="h-7 w-7" data-id="element-3024" data-mp="2v426rotrrkha" />
          </span>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-ville-navy" data-id="element-3025" data-mp="3023qmbz0rntt">Demande transmise</h1>
          <p className="mt-2 text-[17px] text-slate-600" data-id="element-3026" data-mp="350y89al936cc">
            Votre demande « {created.title} » porte le numéro {created.reference}. Elle a été transmise au service{' '}
            {created.service}, qui vous répondra dans les 2 jours ouvrables.
            {notify && ' Vous serez avisée à chaque changement de statut.'}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-3027" data-mp="1eqbq7ilgr3qr">
            <Link to={`/mes-demandes/${created.id}`} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-3028" data-mp="1jn26a5x9pzc6">
              Suivre ma demande
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3029" data-mp="3qbuhfdmwqvox" />
            </Link>
            <Link to="/mes-demandes" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3030" data-mp="32m05qzebrd69">
              Retour à mes demandes
            </Link>
          </div>
        </div>
      </main>;
  }
  return <main className="bg-white" data-id="element-3031" data-mp="mvnlxuenc4r8">
      <div className="mx-auto max-w-[900px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-3032" data-mp="muc9z7b1rqp1">
        <nav aria-label="Fil d’Ariane" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-3033" data-mp="24silctv4v52g">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-3034" data-mp="29sdbuwazxsmz">
            Accueil
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3035" data-mp="1vtwxqkat0zut" />
          <Link to="/mes-demandes" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-3036" data-mp="20qncn4avhpen">
            Mes demandes
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3037" data-mp="2oj45m9w5f1sq" />
          <span aria-current="page" data-id="element-3038" data-mp="2shbaa6dc8cnk">Nouvelle demande</span>
        </nav>

        <header className="mt-4" data-id="element-3039" data-mp="2erq11npcbbsq">
          <h1 className="text-3xl font-extrabold tracking-tight text-ville-navy lg:text-4xl" data-id="element-3040" data-mp="2eqonnckuqd7u">Nouvelle demande</h1>
          <p className="mt-2 max-w-2xl text-[17px] text-slate-600" data-id="element-3041" data-mp="2joggc8tbm3d3">
            Signalez un problème ou faites une demande de service. Les champs marqués d’un astérisque sont
            obligatoires.
          </p>
        </header>

        <form noValidate onSubmit={submit} className="mt-8 space-y-6" data-id="element-3042" data-mp="3df1vmfgknyw2">
          {/* 1. Catégorie */}
          <fieldset className="rounded-3xl border border-slate-200 p-6 sm:p-8" data-id="element-3043" data-mp="3idwfzlu6tvia">
            <legend className="px-1 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3044" data-mp="3ibsc33wagwi0">
              1. Type de demande{requiredMark}
            </legend>

            {emergency ? <div role="alert" className="mt-2 rounded-2xl border-l-4 border-red-600 bg-red-50 p-5 sm:p-6" data-id="element-3045" data-mp="38i1qc6rjr060">
                <p className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" data-id="element-3046" data-mp="2umqzd4gfaz6x">
                  <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-3047" data-mp="2krfnctweg62v" />
                  Urgence
                </p>
                <p className="mt-3 text-lg font-extrabold leading-snug text-ville-navy" data-id="element-3048" data-mp="2ppquuxz824hx">
                  Une urgence ne peut pas être signalée par formulaire.
                </p>
                <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-slate-700" data-id="element-3049" data-mp="2gnhlwcjlqj6n">
                  N’attendez pas le traitement d’une demande en ligne. Composez le 911 si une personne est en danger, ou
                  la ligne d’urgence municipale pour un bris dangereux sur la voie publique.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-3050" data-mp="261kwf661qexl">
                  <a href="tel:911" className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2" data-id="element-3051" data-mp="221ass05q80lw">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-3052" data-mp="2ks1y8qmlf6v0" />
                    Appeler le 911
                  </a>
                  <a href="tel:4509236311" className="inline-flex items-center gap-2 rounded-full border border-ville-navy bg-white px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3053" data-mp="2yt4l35lewl6z">
                    Ligne d’urgence municipale
                  </a>
                  <button type="button" onClick={() => chooseCategory(null)} className="text-sm font-bold text-ville-navy underline underline-offset-4 hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3054" data-mp="2570ayz6gnlbd">
                    Ce n’est pas une urgence, changer de type
                  </button>
                </div>
              </div> : category ?
          // Catégorie choisie : les autres sont masquées, le 2e niveau s'affiche à côté.
          <div className="mt-2 grid gap-5 sm:grid-cols-[200px_minmax(0,1fr)]" data-id="element-3055" data-mp="psursjbbqw7x">
                <div className="flex h-[132px] flex-col items-start gap-3 self-start rounded-2xl border border-ville-navy bg-ville-indigo-soft p-4" data-id="element-3056" data-mp="tsd811s7aj4f">
                  {React.createElement(categoryIcons[category.id], {
                className: 'h-5 w-5 text-ville-indigo'
              })}
                  <span className="text-sm font-bold leading-tight text-ville-navy" data-id="element-3057" data-mp="cjsvs3neibzx">{category.label}</span>
                  <button type="button" onClick={() => chooseCategory(null)} className="mt-auto text-xs font-bold text-ville-indigo underline underline-offset-4 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3058" data-mp="8ih1vlxhv4lx">
                    Changer de type
                  </button>
                </div>

                <div data-id="element-3059" data-mp="r7dxpu8ji569">
                  <p id="type-label" className={labelClass} data-id="element-3060" data-mp="ra01disb80j7">
                    Précisez le problème{requiredMark}
                  </p>
                  <div role="radiogroup" aria-labelledby="type-label" aria-invalid={show('type') || undefined} className={`mt-2 flex flex-wrap gap-2 rounded-2xl ${show('type') ? 'outline outline-2 outline-offset-4 outline-red-500' : ''}`} data-id="element-3061" data-mp="3bxmrrg7ie19a">
                    {category.types.map(option => {
                  const active = type === option;
                  return <button key={option} type="button" role="radio" aria-checked={active} onClick={() => setType(option)} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-navy text-white' : 'border-slate-200 bg-white text-ville-navy hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo'}`} data-id="element-3062" data-mp="1mg7hyb6c0en6">
                          {active && <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-3063" data-mp="8dqknftb7fp6" />}
                          {option}
                        </button>;
                })}
                  </div>
                  {type === 'Animal perdu' && <LostPetPicker value={petId} onChange={setPetId} invalid={show('pet')} data-id="element-3064" data-mp="36z1gbcq3eez7" />}
                  {type && <FormAtnaHint key={type} type={type} data-id="element-3065" data-mp="3lnxr3hjja63i" />}
                </div>
              </div> : <div role="radiogroup" aria-label="Catégorie" aria-invalid={show('category') || undefined} className={`mt-2 grid grid-cols-2 gap-3 rounded-2xl sm:grid-cols-4 ${show('category') ? 'outline outline-2 outline-offset-4 outline-red-500' : ''}`} data-id="element-3066" data-mp="3gu35g8kdbh1s">
                {manualCategories.map(item => {
              const Icon = categoryIcons[item.id];
              return <button key={item.id} type="button" role="radio" aria-checked={false} onClick={() => chooseCategory(item.id)} className="flex flex-col items-start gap-3 rounded-2xl border border-slate-200 p-4 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3067" data-mp="15vhg7itnbfdj">
                      <Icon className="h-5 w-5 text-slate-500" data-id="element-3068" data-mp="1tll6yplqjkn4" />
                      <span className="text-sm font-bold leading-tight text-ville-navy" data-id="element-3069" data-mp="1tn65srf31pqs">{item.label}</span>
                    </button>;
            })}
                <button type="button" role="radio" aria-checked={false} onClick={() => {
              chooseCategory(null);
              setEmergency(true);
            }} className="col-span-full flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-left transition-colors duration-150 ease-ville hover:border-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600" data-id="element-3070" data-mp="28nlbzdxgayxn">
                  <SirenIcon className="h-5 w-5 shrink-0 text-red-600" aria-hidden="true" data-id="element-3071" data-mp="1l0v96ada30bz" />
                  <span className="text-sm font-bold text-ville-navy" data-id="element-3072" data-mp="1g18zgawns27x">Urgence</span>
                  <span className="text-sm text-slate-600" data-id="element-3073" data-mp="1bzndm025cdll">Danger immédiat pour une personne ou sur la voie publique</span>
                </button>
              </div>}
          </fieldset>

          {!emergency && <>
          {/* 2. Description */}
          <fieldset className="rounded-3xl border border-slate-200 p-6 sm:p-8" data-id="element-3074" data-mp="3plwm7zj00bjz">
            <legend className="px-1 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3075" data-mp="3tmghsynxb740">2. Description</legend>

            <p className="mt-2" data-id="element-3076" data-mp="3ftqsold3lm2o">
              <label htmlFor="description" className={labelClass} data-id="element-3077" data-mp="3fspl4qvajhpp">
                Décrivez la situation{requiredMark}
              </label>
              <textarea id="description" rows={4} value={description} onChange={event => setDescription(event.target.value)} aria-invalid={show('description') || undefined} className={`${baseField} resize-y ${show('description') ? fieldError : fieldOk}`} data-id="element-3078" data-mp="35w4czwnz5qhn" />
            </p>

            <div className="mt-5" data-id="element-3079" data-mp="v4ufww0es1ls">
              <span className={labelClass} data-id="element-3080" data-mp="v5m9y2ecxr9y">Photos ou documents</span>
              <div className="mt-2 flex flex-wrap items-start gap-5" data-id="element-3081" data-mp="103e6xatv03l0">
              <ul className="flex flex-wrap gap-3" data-id="element-3082" data-mp="101l4k7qa45it">
                {files.map((file, index) => {
                    const loading = uploading === file.name;
                    return <li key={`${file.name}-${index}`} className="relative" data-id="element-3083" data-mp="15achcnsj5nlk" data-mpi={index}>
                    <span title={`${file.name} · ${file.size}`} aria-busy={loading || undefined} className="relative block h-24 w-24 overflow-hidden rounded-2xl border border-slate-200" data-id="element-3084" data-mp="1j0pr8vozfcdj">
                      {file.url ? <>
                          <img src={file.url} alt={`Photo jointe : ${file.name}`} className={`h-full w-full object-cover transition-opacity duration-200 ease-ville ${loading ? 'opacity-40' : 'opacity-100'}`} data-id="element-3085" data-mp="1o1ded6a3b92h" />
                          {loading && <span className="absolute inset-x-2 bottom-2" data-id="element-3086" data-mp="372bpqko7u1zb">
                              <span className="sr-only" data-id="element-3087" data-mp="3708pz8x9guhe">Téléversement en cours</span>
                              <span className="block h-1.5 overflow-hidden rounded-full bg-white/80" aria-hidden="true" data-id="element-3088" data-mp="321e7y974b9yc">
                                <motion.span className="block h-full rounded-full bg-ville-navy" initial={{
                                width: '8%'
                              }} animate={{
                                width: '100%'
                              }} transition={{
                                duration: 0.85,
                                ease: [0.23, 1, 0.32, 1]
                              }} data-id="element-3089" data-mp="3btbkbj9v47yn" />
                              </span>
                            </span>}
                        </> : <span className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-ville-mist px-2 text-ville-deep" data-id="element-3090" data-mp="cdewri8yehig">
                          <PaperclipIcon className="h-5 w-5" aria-hidden="true" data-id="element-3091" data-mp="8cbfqsf66ffn" />
                          <span className="w-full truncate text-center text-[11px] font-semibold" data-id="element-3092" data-mp="fuj4pqkrsfab">{file.name}</span>
                        </span>}
                    </span>
                    <button type="button" onClick={() => setFiles(current => current.filter((_, i) => i !== index))} aria-label={`Retirer ${file.name}`} className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3093" data-mp="5z7xruflscd4">
                      <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-3094" data-mp="1hlg4so7tlk72" />
                    </button>
                  </li>;
                  })}
                {files.length < 5 && <li data-id="element-3095" data-mp="1w86eodzm0l49">
                    <button type="button" onClick={simulateUpload} disabled={uploading !== null} className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-slate-300 text-slate-500 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3096" data-mp="1w8ohxukovzlo">
                      <PlusIcon className="h-6 w-6" strokeWidth={1.5} aria-hidden="true" data-id="element-3097" data-mp="18h12q9wsicz8" />
                      <span className="text-xs font-semibold" data-id="element-3098" data-mp="13kan1nowdjec">Téléverser</span>
                    </button>
                  </li>}
              </ul>
              <PhotoAtnaHint type={type} data-id="element-3099" data-mp="ujcfivf92tr0" />
              </div>
            </div>
          </fieldset>

          {/* 3. Emplacement */}
          <fieldset className="rounded-3xl border border-slate-200 p-6 sm:p-8" data-id="element-3100" data-mp="pl14wih4sf7q">
            <legend className="px-1 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3101" data-mp="317uzwdc0tex0">
              3. Emplacement{requiredMark}
            </legend>

            <div className="mt-2 grid gap-3 sm:grid-cols-3" data-id="element-3102" data-mp="367zgs4gucbnr">
              {addressOptions.map(item => {
                const active = addressChoice === item.id;
                return <label key={item.id} className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors duration-150 ease-ville focus-within:ring-2 focus-within:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-3103" data-mp="2igu1priq5nei">
                      <input type="radio" name="adresse" value={item.id} checked={active} onChange={() => setAddressChoice(item.id)} className="mt-0.5 h-4 w-4 accent-ville-navy" data-id="element-3104" data-mp="1zp175so0bu4o" />
                      <span data-id="element-3105" data-mp="1flvbdc26z2n9">
                        <span className="flex items-center gap-1.5 text-sm font-bold text-ville-navy" data-id="element-3106" data-mp="1ucm3kl7p43qw">
                          {item.id === 'position' && <LocateFixedIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-3107" data-mp="1pk2n9ghmiuie" />}
                          {item.label}
                        </span>
                        <span className="block text-sm text-slate-600" data-id="element-3108" data-mp="1ygk0qqdgn4hb">{item.address}</span>
                      </span>
                    </label>;
              })}
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-3109" data-mp="28assvcykwvey">
              {addressChoice === 'autre' && <p data-id="element-3110" data-mp="dym6di08is7c">
                  <label htmlFor="autre-adresse" className={labelClass} data-id="element-3111" data-mp="dze00na9ufuz">
                    Adresse ou intersection{requiredMark}
                  </label>
                  <AddressAutocomplete id="autre-adresse" value={otherAddress} onChange={setOtherAddress} invalid={show('address')} className={`${baseField} ${show('address') ? fieldError : fieldOk}`} data-id="element-3112" data-mp="2po2ahgxnyfgy" />
                </p>}
              <p data-id="element-3113" data-mp="26uew673ev04m">
                <label htmlFor="precision" className={labelClass} data-id="element-3114" data-mp="26twu2tu2hros">
                  Précision sur le lieu
                </label>
                <input id="precision" value={precision} onChange={event => setPrecision(event.target.value)} aria-describedby="precision-aide" className={`${baseField} ${fieldOk}`} data-id="element-3115" data-mp="2auqirpfc3aiq" />
                <span id="precision-aide" className="mt-1 block text-xs text-slate-500" data-id="element-3116" data-mp="3r98lygz9eene">
                  Facultatif · ex. devant l’entrée, côté parc.
                </span>
              </p>
            </div>
          </fieldset>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2" data-id="element-3117" data-mp="3pufnbapm6kfo">
            <div data-id="element-3118" data-mp="3kv30gpyqtdd3">
              <p id="aviser-label" className="flex items-center gap-2 text-sm font-bold text-ville-navy" data-id="element-3119" data-mp="3kujhd5xcwixh">
                <BellIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-3120" data-mp="3fwrpud0miytt" />
                M’aviser de l’avancement
              </p>
              <div role="group" aria-labelledby="aviser-label" className="mt-2 flex flex-wrap gap-2" data-id="element-3121" data-mp="hdkpi9nobq8g">
                {notifyOptions.map(({
                  id,
                  label,
                  Icon
                }) => {
                  const active = channels.includes(id);
                  return <button key={id} type="button" aria-pressed={active} onClick={() => toggleChannel(id)} title={id === 'courriel' ? profile.email : profile.phone} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-navy text-white' : 'border-slate-200 bg-white text-ville-navy hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo'}`} data-id="element-3122" data-mp="2pzmsgupnt12">
                      {active ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-3123" data-mp="1qlwetofs95r8" /> : <Icon className="h-4 w-4" aria-hidden="true" data-id="element-3124" data-mp="1vdxlmuu6m49v" />}
                      {label}
                    </button>;
                })}
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-3125" data-mp="25c3l75simybk">
              <Link to="/mes-demandes" className="rounded-full px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3126" data-mp="25dokt9tot7ga">
                Annuler
              </Link>
              <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-3127" data-mp="34muucw6pk2yq">
                <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-3128" data-mp="27y5efben5kfe" />
                Envoyer ma demande
              </button>
            </div>
          </div>
          </>}
        </form>
      </div>
    </main>;
}